/**
 * Transcript UI for audio asset blocks — annotation-schema segments
 * (`annotation/*` entities linked via `annotation/recording`, see
 * @wovin/tranz docs/annotation-schema.md) produced by the Transcribe action.
 *
 * Split into a tiny inline toggle chip (lives in the AudioPlayer row, so a
 * collapsed transcript costs no extra line) and the expandable segment
 * panel below. AudioPlayer owns the open/closed signal and composes both.
 *
 * NOT a kid-renderer: the block's real children stay free for the
 * structured notes the Structure action creates.
 *
 * Discovery + attribute access via useLiveEntityCollection (subscription-
 * based) so segments appear the moment the Transcribe action inserts them —
 * plain queries in memos are NOT reactive to thread inserts.
 */

import type { EntityID } from '@wovin/core'
import type { ThreadOnlyCurrentNoDeleted } from '@wovin/core/thread'
import type { Accessor, Component } from 'solid-js'
import { useLiveEntityCollection } from '@wovin/hooks-solid'
import classNames from 'classnames'
import { createMemo, For, Show } from 'solid-js'
import { formatElapsed } from '../audio/recorder'
import { isStructuring } from '../audio/structure'
import { isTranscribing } from '../audio/transcribe'
import { Iconify, Spinner } from './mini-components'

export interface TranscriptSegmentVM {
	startMs: number
	transcript: string
	diarization: string | null
}

/**
 * Reactive annotation segments of a recording block, sorted by startMs.
 * Shared by the transcript UI and BulletMenu/AudioActionsMenu gating — the
 * underlying liveEntityCollection is memoized per (thread, pattern, attrs),
 * so all consumers share one subscription per block.
 */
export function useAnnotationSegments(blockID: EntityID, thread: ThreadOnlyCurrentNoDeleted) {
	const annotations = useLiveEntityCollection(
		{ at: 'annotation/recording', vl: blockID },
		['annotation/startMs', 'annotation/endMs', 'annotation/transcript', 'annotation/diarization'] as const,
		{ thread, stripAtPrefix: true },
	)
	return createMemo<TranscriptSegmentVM[]>(() => {
		const result: TranscriptSegmentVM[] = []
		for (const attrs of annotations().values()) {
			const transcript = attrs.transcript
			if (typeof transcript !== 'string' || !transcript.trim()) continue
			result.push({
				startMs: Number(attrs.startMs ?? 0),
				transcript,
				diarization: attrs.diarization != null ? String(attrs.diarization) : null,
			})
		}
		return result.sort((a, b) => a.startMs - b.startMs)
	})
}

/**
 * Inline chip for the AudioPlayer row: transcript toggle, or busy spinner
 * while a pipeline action runs. Renders nothing when idle without segments.
 */
export const TranscriptToggle: Component<{
	blockID: EntityID
	segments: Accessor<TranscriptSegmentVM[]>
	open: Accessor<boolean>
	onToggle: () => void
}> = (props) => {
	const busyLabel = createMemo(() => {
		if (isTranscribing(props.blockID)) return 'Transcribing…'
		if (isStructuring(props.blockID)) return 'Structuring…'
		return null
	})
	return (
		<>
			<Show when={busyLabel()}>
				<span class='flex items-center gap-1 text-xs opacity-70'>
					<Spinner size='0.9rem' />
					{busyLabel()}
				</span>
			</Show>
			<Show when={!busyLabel() && props.segments().length}>
				<button
					type='button'
					onClick={() => props.onToggle()}
					class='appearance-none flex cursor-pointer items-center gap-0.5 border-none bg-transparent p-0 text-inherit text-xs opacity-50 transition-opacity hover:opacity-100'
					title={`${props.segments().length} transcript segment${props.segments().length !== 1 ? 's' : ''}`}
				>
					<Iconify name='caret-right-bold' size={3} class={classNames('transition-transform', props.open() && 'rotate-90')} />
					transcript
				</button>
			</Show>
		</>
	)
}

/** Expanded segment list (timestamp + diarization label per segment). */
export const TranscriptSegments: Component<{ segments: Accessor<TranscriptSegmentVM[]> }> = (props) => {
	return (
		<div class='mt-0.5 flex flex-col gap-0.5 border-l-2 border-neutral-300 pl-2 text-sm dark:border-neutral-600'>
			<For each={props.segments()}>
				{seg => (
					<div>
						<span class='mr-2 text-xs font-mono opacity-50'>{formatElapsed(seg.startMs)}</span>
						<Show when={seg.diarization}>
							<span class='mr-1 text-xs opacity-60'>[{seg.diarization}]</span>
						</Show>
						<span>{seg.transcript}</span>
					</div>
				)}
			</For>
		</div>
	)
}
